import React, { useEffect } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { useUser } from '@paintscout/ui-business'
import { useAuth } from 'react-auth0'

// isSubscribed comes from the Auth0 ID token's app_metadata.company, which is only rebuilt fresh
// (from the live client doc) at token issuance — login or a silent token refresh — not pushed to
// already-open tabs. A Chargebee cancellation webhook can land at any time, so a session opened
// before it fires keeps showing the account as fully functional for the rest of its (potentially
// multi-day) lifetime even though PaintScout's own backend already recorded it as cancelled
// (#22013). Rechecking every 5 minutes bounds that window without adding meaningful auth traffic.
const CHURN_RECHECK_INTERVAL_MS = 5 * 60 * 1000

/**
 * Guard component that redirects users on an inactive company to the billing page.
 *
 * Fires for any company that is not subscribed (`!isSubscribed`) — this covers the
 * strict churned case (cancelled + term ended) as well as a company simply flagged
 * inactive (e.g. `company.active === false` set by the Chargebee cancellation webhook,
 * regardless of subscription status or term end). Only settings/billing routes are
 * allowed through so the user can still reach billing to reactivate. nonBilling
 * companies and active trials are subscribed, so they pass through untouched;
 * trial-expired users are handled separately by TrialExpiredGuard.
 */
export default function ChurnedGuard({ children }: { children: React.ReactNode }) {
  const { isSubscribed, isSuperadmin, isSupport } = useUser()
  const { authenticate } = useAuth()
  const location = useLocation()

  useEffect(() => {
    // Only a currently-subscribed session needs rechecking — one already redirected to billing
    // has nothing further to gain from refreshing on this timer.
    if (!isSubscribed) return

    const interval = setInterval(() => {
      authenticate({ ignoreCache: true }).catch(() => {
        // Best-effort — a failed refresh just leaves the existing (stale) claims in place until
        // the next tick or a user-triggered authenticate() call elsewhere in the app.
      })
    }, CHURN_RECHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [isSubscribed, authenticate])

  // Allow access to settings/billing routes when inactive
  const isBillingRoute = location.pathname.startsWith('/settings')

  // If the company is inactive and not on a billing route, redirect to billing.
  // Superadmin and support bypass this block as they are internal staff.
  if (!isSubscribed && !isBillingRoute && !isSuperadmin && !isSupport) {
    return <Redirect to="/settings?section=billing-plan" />
  }

  return <>{children}</>
}
